/** /activities/:id/:tab? — the activity record (plan §1–§3, §6–§8, §10.3): a place guests see
 *  in the app and tours copy into their days, read first and edited in place on the records
 *  core — the booking, order and user records' sibling.
 *
 *  - **Header** — `ActivityRecordHeader`: what the app shows as the title, the type and
 *    category chips, the identity subtitle; the Actions menu (Content: Duplicate… — a
 *    background task followed in place, `DuplicatePopover`, its end announced by the app shell
 *    wherever the operator is by then — and Refresh from Google… — a
 *    proposal put into the draft, never a write, `GoogleRefreshPopover`; Investigate: Ext
 *    IDs, Open Usage, Open in dashboard; Danger: Delete…) and **Edit** (`E`) / **Close
 *    editor** (`Esc`).
 *    No console_activities_manage → no Edit; the `ConsoleCatalogEditEnabled` switch off →
 *    Edit disabled, "Editing is switched off"; an activity without an operator → Edit
 *    disabled, "No owning operator".
 *  - **Tabs** (URL, keeping `?edit`): Activity (the record) · Images N · Usage · History
 *    (console_audit_view). Old slugs redirect: `general` → the record, `used-by` → `usage`.
 *    Usage is the full, paged used-by in Where it's used's groups, with the tours' upcoming
 *    departures from the used-by summary (`ActivityUsageTab`).
 *  - **Fact band** (record tab) — Type & category · Place · Used by · Content · Source.
 *  - **Where it's used** — the first panel, full width (key `1`); its rows and the used-by
 *    summary are asked for only once it has been on screen.
 *  - **Cards** — FieldSlots over the registry, the values from the draft's baselines:
 *    edit_context's, else the detail's (the same values), so the cards show at once. After
 *    Nearby, the **Documents & links** panel (O12): immediate, outside the draft, the same in
 *    both modes — list, upload, add a link, rename, reorder, remove, and how many tour stops
 *    inherit them (`DocumentsPanel`). Keys `2`–`9`, then `0`, reach the cards and the panel in
 *    the order they stand: one column in page order, two columns the left column, then the
 *    right, then Technical.
 *  - **Edit mode** — exactly the record editor: `?edit=1` (or `E`) once the edit catalog and
 *    edit_context are here; the draft (`DraftProvider kind="activity"`, seeded from
 *    edit_context `baselines`) and the editing session are PAGE-level, so a tab switch keeps
 *    both and only leaving the activity is guarded. FieldSlots swap in their inputs in place
 *    (Zero-Re-Layout); fields a feed may overwrite carry its verdict (D4). The preview plans
 *    the draft (the ledger's lines, the app cache, the app reach — D8 — and the time zone
 *    queued by new coordinates); Save is the apply, compare-and-set per field: a 409 turns
 *    the drifted fields stale (Keep mine / Take theirs), a 400 files its errors under their
 *    fields, a save ends in the receipt with the audit link and re-seeds the page. A
 *    Custom ID change on an activity an import has touched asks the re-key question (O11)
 *    under the field and in the ledger: Re-key answers it, Cancel takes the change back.
 *  - **The dock** (O5) — from 1100 px of content a 380 px column beside the content, in both
 *    modes and on every tab but History (as the tour's): **Preview | Changes N** (the phone
 *    renders the draft's changes — O4 — on request), so pressing E on Images or Usage moves
 *    nothing either. History has no phone: it docks the plain ledger in edit mode only. Below
 *    1100 px the ledger is the bottom sheet and the header's Preview (not on History) opens the
 *    phone in a drawer.
 *  - **States** — the page's shape while loading (the band, Where it's used, the cards in
 *    their rows, the dock's column; on Images and Usage one panel beside the dock's column, on
 *    History one panel); the catalog records' empty states for a missing
 *    activity (404) or one the operator may not see (403); a load error with Retry; a failed
 *    refresh keeps the loaded copy under a notice; a draft restored from sessionStorage (or
 *    kept after leaving edit mode) is announced in view mode; an import of the activity's
 *    source running now is announced (it may save over an edit); an editor that could not
 *    load says so, with Retry.
 *  - **Delete** — Actions → Danger → Delete…, disabled up front while any relation Where
 *    it's used marks "blocks Delete" has a known count: the detail's catalog itineraries and
 *    line-set stops, and — once the used-by summary or Where it's used has counted them — the
 *    tours, orphan day plans and unlisted trips (`deleteGateCounts`); the reason names each.
 *    Otherwise it confirms first, saying that a draft's unsaved changes go with it and that
 *    the server still refuses it while travellers' trips use it (`409 activity_in_use`, the
 *    backstop; the rule's words are `whereUsedModel`'s). A delete clears the
 *    draft and its stored copy (after the server's success) before leaving, so the
 *    unsaved-changes guard never asks about an activity that no longer exists. */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SyncIcon from '@mui/icons-material/Sync';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Navigate, Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as activitiesApi from '@/api/activities';
import type { ActivityDetail } from '@/api/activities';
import {
  toActivityChangeset,
  useActivityDetail,
  useActivityEditCatalog,
  useActivityEditContext,
  useActivityUsedBySummary,
  useWhereUsedPages,
  type ActivityRecordEditContext,
} from '@/api/activityRecord';
import { ConsoleApiError } from '@/api/client';
import { makeGalleryApi } from '@/api/media';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { ImageGallery } from '@/components/ImageGallery';
import type { PreviewApplied } from '@/components/MobilePreview';
import { useSnackbar } from '@/components/Snackbar';
import {
  ACTIVITY_RECORD,
  CatalogHeaderSkeleton,
  CatalogLoadError,
  CatalogRefreshError,
} from '@/features/catalog/CatalogLoadStates';
import { CATALOG_EDITOR_LOAD_FAILED, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { FactBandSkeleton } from '@/features/records/band/FactBand';
import type { RecordChangeset, RecordKey } from '@/features/records/draft/changeSet';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { RecordEmptyState, RecordPageSkeleton } from '@/features/records/page/RecordStates';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  loadsRecordTab,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';
import { PREVIEW_DOCK_WIDTH, PreviewDock, PreviewDockSkeleton } from '@/features/records/preview/PreviewDock';
import { PreviewDrawer } from '@/features/records/preview/PreviewDrawer';
import { RecordPreviewPane } from '@/features/records/preview/RecordPreviewPane';
import { RecordPreviewProvider } from '@/features/records/preview/RecordPreviewProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';
import { activitiesHref, cond, extIdsHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import {
  activityCardAnchorId,
  activityCardOrder,
  activityCardRows,
  activityCardSkeleton,
  ActivityCardsView,
  activityColumns,
} from './cards/ActivityCardsView';
import type { ActivityDisplayContext } from './cards/displays';
import { activitySectionAnchorId } from './cards/layout';
import { ACTIVITY_MANAGE_PERM, ACTIVITY_TIMEZONE, activityBaselines, useActivityEditorConfig } from './editorConfig';
import { DuplicatePopover } from './actions/DuplicatePopover';
import { GoogleRefreshPopover, googleRefreshBlockedReason } from './actions/GoogleRefreshPopover';
import { ActivityFactBand } from './parts/ActivityFactBand';
import { type ActivityCardKey, type ActivityRecordTab } from './parts/activityFactBandModel';
import { activityTitle } from './parts/activityNames';
import { ActivityRecordHeader } from './parts/ActivityRecordHeader';
import { ActivityUsageTab } from './parts/ActivityUsageTab';
import { DocumentsPanel } from './parts/DocumentsPanel';
import { WHERE_USED_ANCHOR_ID, WhereUsedPanel, WhereUsedPanelSkeleton } from './parts/WhereUsedPanel';
import {
  deleteDisabledReason,
  deleteGateCounts,
  deleteRefusedBy,
  WHERE_USED_FIRST,
  whereUsedGroups,
} from './parts/whereUsedModel';

/** Old tab slugs (bookmarks, links from other pages) → the tab that took them over. */
export const ACTIVITY_SLUG_REDIRECTS: Readonly<Record<string, ActivityRecordTab>> = {
  general: 'activity',
  'used-by': 'usage',
};

const SECTION_LABEL: Readonly<Record<ActivityRecordTab, string>> = {
  activity: 'Activity',
  images: 'Images',
  usage: 'Usage',
  history: 'History',
};

const TABS_LABELS = { label: 'Activity sections', refreshingLabel: 'Refreshing the activity' };

/** The delete the server refused (`409 activity_in_use`), in the delete rule's words: what may
 *  still refuse it once the gate let it through (`deleteRefusedBy` — the travellers' trips,
 *  and the tours while they were not counted). */
export function activityInUseMessage(toursChecked = true): string {
  return `This activity can’t be deleted: ${deleteRefusedBy(toursChecked)} still use it. Open Usage to see where.`;
}

/** The delete's confirmation: the activity by the name the app shows, what happens to the
 *  draft's unsaved changes (they go too), and what may still refuse it (the rule's server
 *  half) — saying so when the tours using it have not been counted (the used-by summary is
 *  not in: Delete is not disabled for them yet). */
export function activityDeleteMessage(title: string, unsaved = 0, toursChecked = true): string {
  const draft =
    unsaved === 1 ? ' Your unsaved change goes with it.' : unsaved > 1 ? ` Your ${unsaved} unsaved changes go with it.` : '';
  const unchecked = toursChecked ? '' : 'The tours using it have not been checked yet. ';
  return (
    `“${title}” will be deleted.${draft} This cannot be undone. ` +
    `${unchecked}The server still refuses it while ${deleteRefusedBy(toursChecked)} use it.`
  );
}

/** The tabs a viewer sees, with Images' count. */
export function activityTabs(
  a: Pick<ActivityDetail, 'image_count'>,
  canViewHistory: boolean,
): { id: ActivityRecordTab; label: string }[] {
  return [
    { id: 'activity', label: 'Activity' },
    { id: 'images', label: a.image_count ? `Images ${a.image_count.toLocaleString('en-US')}` : 'Images' },
    { id: 'usage', label: 'Usage' },
    ...(canViewHistory ? [{ id: 'history' as const, label: 'History' }] : []),
  ];
}

const BACK = (
  <Button component={RouterLink} to={ACTIVITY_RECORD.gridHref} startIcon={<ArrowBackIcon />} size="small">
    Back to activities
  </Button>
);

export function ActivityRecordPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`Activity #${idStr ?? ''} not found`} action={BACK}>
        That is not an activity id.
      </RecordEmptyState>
    );
  }
  const redirect = tab ? ACTIVITY_SLUG_REDIRECTS[tab] : undefined;
  if (redirect) {
    return <Navigate replace to={`/activities/${id}${redirect === 'activity' ? '' : `/${redirect}`}${location.search}`} />;
  }
  return <ActivityRecordLoader key={id} id={id} tabParam={tab} />;
}

/** The band's cells (Type & category · Place · Used by · Content · Source). */
const BAND_CELLS = 5;

/** The page's shape while the activity loads, so the loaded page lands where the skeleton
 *  stood (Zero-Re-Layout): the header (title, subtitle, the Actions and Edit buttons' places)
 *  and the tabs; on the record tab the fact band (its own chrome and five cells), Where it's
 *  used, the cards in the loaded cards' rows — two columns only where the page will have two —
 *  and, from 1100 px of content, the dock's 380 px column beside them (its Preview | Changes
 *  switch for a manager). Images and Usage: no band, one panel, the same dock column beside
 *  it. History: one panel, no dock (it docks the ledger in edit mode only). `edit`: the
 *  operator will see Edit. */
function ActivityRecordLoading({ edit, tab }: { edit: boolean; tab: 'record' | 'docked' | 'history' }) {
  const dock = { width: PREVIEW_DOCK_WIDTH, node: <PreviewDockSkeleton segments={edit} /> };
  return (
    <Box role="status" aria-label="Loading the activity" data-activity-loading>
      <CatalogHeaderSkeleton edit={edit} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      {tab === 'record' ? (
        <>
          <FactBandSkeleton cells={BAND_CELLS} />
          <RecordPageSkeleton
            layout={activityColumns}
            rows={activityCardRows}
            card={activityCardSkeleton}
            lead={<WhereUsedPanelSkeleton />}
            dock={dock}
          />
        </>
      ) : tab === 'docked' ? (
        <RecordPageSkeleton
          layout={activityColumns}
          rows={() => []}
          card={() => null}
          lead={<Skeleton variant="rounded" height={320} data-tab-skeleton />}
          dock={dock}
        />
      ) : (
        <Skeleton variant="rounded" height={320} data-tab-skeleton />
      )}
    </Box>
  );
}

function ActivityRecordLoader({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const nav = useRecordNav<ActivityRecordTab>(`/activities/${id}`, 'activity');
  const detailQ = useActivityDetail(id, { retry: retryUnlessMissing });
  const detail = detailQ.data;
  if (!detail) {
    const history = hasPerm('console_audit_view') ? ['history'] : [];
    return detailQ.error ? (
      <CatalogLoadError kind={ACTIVITY_RECORD} id={id} error={detailQ.error} onRetry={() => void detailQ.refetch()} />
    ) : (
      <ActivityRecordLoading
        edit={hasPerm(ACTIVITY_MANAGE_PERM)}
        tab={
          loadsRecordTab(tabParam, ['images', 'usage', ...history])
            ? 'record'
            : tabParam === 'history'
              ? 'history'
              : 'docked'
        }
      />
    );
  }
  const tabs = activityTabs(detail, hasPerm('console_audit_view'));
  const activeTab: ActivityRecordTab = tabs.find((t) => t.id === tabParam)?.id ?? 'activity';
  return (
    <ActivityRecordLoaded
      detail={detail}
      tabs={tabs}
      activeTab={activeTab}
      nav={nav}
      fetching={detailQ.isFetching}
      refreshError={detailQ.error}
      refetch={() => void detailQ.refetch()}
    />
  );
}

interface LoadedProps {
  detail: ActivityDetail;
  tabs: { id: ActivityRecordTab; label: string }[];
  activeTab: ActivityRecordTab;
  nav: RecordNav<ActivityRecordTab>;
  fetching: boolean;
  /** A refresh failed: the loaded copy stays under a notice. */
  refreshError: unknown;
  refetch: () => void;
}

/** Why the editor cannot open: it could not load (the catalog records' words). */
export const EDITOR_LOAD_FAILED = CATALOG_EDITOR_LOAD_FAILED;

/** What a render of the phone applies: what Save would send (O4) — the activity's changes
 *  and the re-key answer with the Custom ID it confirms. */
function previewChangeset(body: RecordChangeset): PreviewApplied {
  const wire = toActivityChangeset(body);
  return { changes: wire.changes, ...(wire.options ? { options: wire.options } : {}) };
}

interface PendingFocus {
  rec: RecordKey;
  path: string;
}

interface EditorProps {
  /** May edit now (the permission, the switch, an operator, the editor loaded). */
  canEdit: boolean;
  /** The edit catalog and edit_context are here. */
  ready: boolean;
  editing: boolean;
  /** The permission alone: Edit is offered at all. */
  mayManage: boolean;
  editingDisabled: boolean;
  /** With the permission and the switch on, why Edit is blocked (no operator, the editor
   *  failed to load). */
  editBlockedReason: string | null;
  editContext: ActivityRecordEditContext | null;
  editorError: unknown;
  retryEditor: () => void;
  pendingFocus: MutableRefObject<PendingFocus | null>;
}

/** The records core around the loaded activity: the draft (edit_context's baselines, the
 *  detail's until they arrive), the editing session and the page's one preview. */
function ActivityRecordLoaded(props: LoadedProps) {
  const { detail, nav, activeTab } = props;
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const [params] = useSearchParams();
  const catalogQ = useActivityEditCatalog();
  const ctxQ = useActivityEditContext(id);
  const catalog = catalogQ.data ?? null;
  const ctx = ctxQ.data ?? null;
  const ready = catalog !== null && ctx !== null;
  // Only a failure that keeps the editor out: a failed refresh leaves the loaded data in use.
  const editorError = ready ? null : (catalogQ.error ?? ctxQ.error ?? null);

  // A ledger row for a field that is not on screen: back to the record tab, then focus it
  // once its FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: RecordKey, path: string) => {
      if (tabRef.current === 'activity') return;
      pendingFocus.current = { rec, path };
      goTab('activity');
    },
    [goTab],
  );

  const detailBaselines = useMemo(() => activityBaselines(detail), [detail]);
  const config = useActivityEditorConfig({ detail, catalog, editContext: ctx, onFocusMissing });

  const mayManage = hasPerm(ACTIVITY_MANAGE_PERM) && (ctx ? ctx.permissions.console_activities_manage : true);
  const editingDisabled = catalog?.enabled === false || ctx?.flags.catalog_edit_enabled === false;
  const editBlockedReason = !detail.tenant ? CATALOG_NO_OPERATOR : editorError ? EDITOR_LOAD_FAILED : null;
  const canEdit = mayManage && !editingDisabled && editBlockedReason === null;
  const editing = canEdit && ready && params.get('edit') === '1';
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);

  const editor: EditorProps = {
    canEdit,
    ready,
    editing,
    mayManage,
    editingDisabled,
    editBlockedReason,
    editContext: ctx,
    editorError,
    retryEditor: () => {
      void catalogQ.refetch();
      void ctxQ.refetch();
    },
    pendingFocus,
  };
  return (
    <DraftProvider
      kind="activity"
      recordId={id}
      baselines={ctx?.baselines ?? detailBaselines}
      baselineTs={ctx?.generated_ts ?? detail.updated_ts}
    >
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <RecordPreviewProvider
          entity="activity"
          id={id}
          canDraft={mayManage && ready && !editingDisabled}
          updatedTs={detail.updated_ts ?? null}
          changeset={previewChangeset}
        >
          <ActivityRecordShell {...props} {...editor} />
        </RecordPreviewProvider>
      </RecordEditorProvider>
    </DraftProvider>
  );
}

/** Scroll an anchor into view and focus it (a card, the Where it's used panel). */
function reveal(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  el.focus({ preventScroll: true });
}

/** What the number keys reach on the record tab: `1` Where it's used, then `2`–`9` and `0`
 *  the cards and the Documents & links panel in the order they stand (`activityCardOrder`). */
export function activityKeyTargets(columns: 1 | 2): string[] {
  return [WHERE_USED_ANCHOR_ID, ...activityCardOrder(columns).map(activityCardAnchorId)];
}

function ActivityRecordShell({
  detail,
  tabs,
  activeTab,
  nav,
  fetching,
  refreshError,
  refetch,
  canEdit,
  ready,
  editing,
  mayManage,
  editingDisabled,
  editBlockedReason,
  editContext,
  editorError,
  retryEditor,
  pendingFocus,
}: LoadedProps & EditorProps) {
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const canManage = hasPerm(ACTIVITY_MANAGE_PERM);
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { goTab, hrefFor, setParam } = nav;
  const onRecord = activeTab === 'activity';
  // Every tab but History has the phone: the dock stands there in both modes.
  const hasPreview = activeTab !== 'history';
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Duplicate…: the popover's anchor (its run lives outside the page — `duplicateRuns`).
  const [duplicateAnchor, setDuplicateAnchor] = useState<HTMLElement | null>(null);
  // Refresh from Google…: the proposal's anchor.
  const [googleAnchor, setGoogleAnchor] = useState<HTMLElement | null>(null);

  // ---- the editor ------------------------------------------------------------------------
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  // Deleted: the draft went with the activity, and the page is leaving.
  const [deleted, setDeleted] = useState(false);
  // Only leaving the activity is guarded: its tabs share the draft.
  const { prompt } = useDraftUnsavedGuard(`/activities/${id}`, { enabled: !deleted });
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);

  // A ledger row for a field on another tab lands here: focus it once the cards render.
  useEffect(() => {
    if (!onRecord || !pendingFocus.current) return undefined;
    // Taken when the frame runs: an update in between (a preview arriving) re-runs this
    // effect, whose cleanup cancels the frame — the pending focus must survive that.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onRecord, editor, pendingFocus]);

  // ---- Where it's used: asked for once the panel has been on screen --------------------
  const [whereRef, seen] = useSeenOnce<HTMLDivElement>();
  const whereUsed = useWhereUsedPages(id, { enabled: seen, first: WHERE_USED_FIRST });
  const summaryQ = useActivityUsedBySummary(id, { enabled: seen });

  // ---- the preview drawer (below 1100 px of content) ------------------------------------
  const narrow = contentWidth > 0 && contentWidth < SHEET_BELOW_PX;
  const [previewOpen, setPreviewOpen] = useState(false);
  // Closed by the page, not the operator: widening past 1100 px (the dock takes over, and
  // the focus goes to it) or opening History.
  const [autoClosed, setAutoClosed] = useState(false);
  useEffect(() => {
    if (previewOpen && (!narrow || !hasPreview)) {
      setAutoClosed(!narrow);
      setPreviewOpen(false);
    }
  }, [narrow, hasPreview, previewOpen]);
  const focusDock = useCallback(() => {
    setAutoClosed(false);
    const aside = document.querySelector<HTMLElement>(`[data-record-page="${id}"] [data-ledger-slot] aside`);
    if (!aside) return;
    if (!aside.hasAttribute('tabindex')) aside.setAttribute('tabindex', '-1');
    aside.focus();
  }, [id]);

  // `E` opens the editor; the number keys are the record tab's only (elsewhere — History, …
  // — none is registered). In edit mode the ledger binds ⌘S, ⌘Z / ⇧⌘Z and Esc.
  const keyTargets = useMemo(() => activityKeyTargets(activityColumns(contentWidth).columns), [contentWidth]);
  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onRecord
        ? {
            onStage: (i: number) => {
              const target = keyTargets[i];
              if (target) reveal(target);
            },
          }
        : {}),
    },
    { stageCount: onRecord ? keyTargets.length : 0 },
  );

  // What the delete gate knows: the detail's counts, the summary's tours and Where it's used's
  // relations (each total, once its pages are in).
  const usedByGroups = useMemo(() => (whereUsed.pages ? whereUsedGroups(whereUsed.pages) : null), [whereUsed.pages]);
  const gateCounts = useMemo(
    () => deleteGateCounts(detail.usage, summaryQ.data, usedByGroups),
    [detail.usage, summaryQ.data, usedByGroups],
  );
  // Whether the gate counted the tours: the confirmation and a refused delete name them among
  // what may still refuse it until then.
  const toursChecked = gateCounts.demo_group_tour != null;
  const deleteMutation = useMutation({
    mutationFn: () => activitiesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Activity deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['activities'] });
      // The draft (and its stored copy) goes with the activity; the page leaves once the
      // guard has let go of it (below).
      dispatch({ type: 'RESET' });
      setDeleted(true);
    },
    onError: (e) => {
      // 409 activity_in_use → what refused it (the delete rule's words); otherwise the server's message.
      if (e instanceof ConsoleApiError && e.status === 409) {
        const code = (e.body as { code?: string } | undefined)?.code;
        snackbar.show(code === 'activity_in_use' ? activityInUseMessage(toursChecked) : e.message, 'error');
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  // After the guard's own effects (it is armed above): leaving is no longer blocked.
  useEffect(() => {
    if (deleted) navigate('/activities');
  }, [deleted, navigate]);

  const tenantId = detail.tenant?.id ?? null;
  const googleBlocked = googleRefreshBlockedReason({ editingDisabled, detail, editBlockedReason, ready });
  const onGoogleApplied = (count: number) => {
    setGoogleAnchor(null);
    if (!editing) enterEdit();
    snackbar.show(
      `${count === 1 ? '1 change' : `${count} changes`} from Google ${count === 1 ? 'is' : 'are'} in the draft — check, then save.`,
      'info',
    );
  };
  // Every relation that blocks Delete, once counted (the summary and Where it's used load
  // together); until the tours are — or when they could not be — the confirmation says so.
  const deleteBlocked = deleteDisabledReason(gateCounts);
  const actionGroups: RecordActionGroup[] = [
    {
      key: 'content',
      label: 'Content',
      items: mayManage
        ? [
            // A copy needs an operator to own it; not the edit switch.
            ...(detail.tenant
              ? [
                  {
                    key: 'duplicate',
                    label: 'Duplicate…',
                    hint: 'background task',
                    onSelect: (anchor: HTMLElement | null) => setDuplicateAnchor(anchor),
                  },
                ]
              : []),
            // A proposal for the draft: disabled, with the reason, while it cannot be taken.
            googleBlocked
              ? { key: 'google', label: 'Refresh from Google…', disabled: true, description: googleBlocked }
              : {
                  key: 'google',
                  label: 'Refresh from Google…',
                  hint: 'fills the draft',
                  onSelect: (anchor: HTMLElement | null) => setGoogleAnchor(anchor),
                },
          ]
        : [],
    },
    {
      key: 'investigate',
      label: 'Investigate',
      items: [
        {
          key: 'ext-ids',
          label: 'Ext IDs',
          hint: 'mappings grid',
          onSelect: () => navigate(extIdsHref([cond('model', 'activity'), cond('target', String(id))], tenantId)),
        },
        { key: 'usage', label: 'Open Usage', hint: 'every record using it', onSelect: () => goTab('usage') },
        // A real link: cmd-clickable, a new tab without the opener.
        ...(detail.dashboard_url
          ? [{ key: 'dashboard', label: 'Open in dashboard', hint: 'new tab', href: detail.dashboard_url }]
          : []),
      ],
    },
    ...(canManage
      ? [
          {
            key: 'danger',
            label: 'Danger',
            // Disabled up front while what the detail counts holds it; the way to it beneath.
            items: deleteBlocked
              ? [
                  { key: 'delete', label: 'Delete…', danger: true, disabled: true, description: deleteBlocked },
                  { key: 'delete-usage', label: 'Open Usage', hint: 'what blocks Delete', onSelect: () => goTab('usage') },
                ]
              : [{ key: 'delete', label: 'Delete…', danger: true, onSelect: () => setConfirmDelete(true) }],
          },
        ]
      : []),
  ];

  const tabIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);
  const display = useMemo<ActivityDisplayContext>(() => ({ detail, links: { tenantId } }), [detail, tenantId]);
  const onCard = useCallback((card: ActivityCardKey) => reveal(activitySectionAnchorId(card)), []);
  const usageHref = hrefFor('usage');

  return (
    <Box data-record-page={id}>
      <ActivityRecordHeader
        detail={detail}
        section={SECTION_LABEL[activeTab]}
        canEdit={mayManage}
        editingDisabled={editingDisabled}
        // No operator: nothing can edit it, the dashboard included (its banner says so).
        editBlockedReason={editBlockedReason}
        editing={editing}
        onEdit={enterEdit}
        onClose={editor.requestClose}
        actionGroups={actionGroups}
        {...(narrow && hasPreview ? { onPreview: () => setPreviewOpen(true), previewOpen } : {})}
      />

      {Boolean(refreshError) && <CatalogRefreshError kind={ACTIVITY_RECORD} error={refreshError} onRetry={refetch} />}
      {!detail.tenant && (
        <Alert severity="info" sx={{ mb: 2 }} data-orphan-banner>
          This activity has no owning operator: no tour or app can reach it, and it can’t be edited.
          {canManage ? ' Delete it from the Actions menu if nothing needs it.' : ''}
        </Alert>
      )}
      {Boolean(editorError) && mayManage && detail.tenant && !editingDisabled && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-editor-error
          action={
            <Button color="inherit" size="small" onClick={retryEditor}>
              Retry
            </Button>
          }
        >
          {EDITOR_LOAD_FAILED}, so this activity can’t be edited yet.
          {editorError instanceof Error && editorError.message ? ` ${editorError.message}` : ''}
        </Alert>
      )}
      {/* The feed's warnings, in the server's words: an import of its source running now
          may save over an edit (§7). */}
      {editContext && editContext.feed.warnings.length > 0 && (
        <Alert severity="info" icon={<SyncIcon />} sx={{ mb: 2 }} data-feed-warnings>
          {editContext.feed.warnings.join(' ')}
        </Alert>
      )}
      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={enterEdit}>
                  Continue editing
                </Button>
              )}
              <Button color="inherit" size="small" onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}>
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, ACTIVITY_TIMEZONE, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={hrefFor} fetching={fetching} {...TABS_LABELS} />

      {onRecord && (
        <Box sx={{ mb: 2 }}>
          <ActivityFactBand
            detail={detail}
            summary={summaryQ.data ?? null}
            tabs={tabIds}
            hrefFor={hrefFor}
            onSelect={onCard}
          />
        </Box>
      )}

      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          // Pressing E on any tab with the phone changes what the dock shows, never the layout.
          dockInView={hasPreview}
          width={PREVIEW_DOCK_WIDTH}
          stickyTop={DOCK_GAP_PX - SCROLLER_PADDING_PX}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => (
            <PreviewDock
              variant={variant}
              editing={editing}
              canEdit={canEdit}
              // History has no phone: the plain ledger docks there in edit mode only.
              preview={hasPreview ? <RecordPreviewPane /> : null}
            />
          )}
        >
          {onRecord && (
            <ActivityCardsView
              detail={detail}
              display={display}
              width={contentWidth}
              lead={
                <Box ref={whereRef}>
                  <WhereUsedPanel
                    usedBy={whereUsed.pages}
                    usageHref={usageHref}
                    failed={whereUsed.failed}
                    onRetry={whereUsed.refetch}
                    activity={detail}
                    usage={detail.usage}
                  />
                </Box>
              }
              documents={
                <DocumentsPanel
                  activityId={id}
                  documentCount={detail.document_count}
                  canManage={mayManage}
                  orphan={!detail.tenant}
                />
              }
            />
          )}
          {activeTab === 'images' && (
            <ImageGallery
              api={makeGalleryApi('activities', id)}
              queryKey={['activities', 'images', id]}
              canManage={canManage}
            />
          )}
          {activeTab === 'usage' && <ActivityUsageTab detail={detail} />}
          {activeTab === 'history' && (
            <HistoryPanel contentType="demo.activity" objectPk={String(id)} onRestored={refetch} />
          )}
        </LedgerDock>
      </Box>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated {formatRelativeTime(detail.updated_ts)} ·{' '}
        <RouterLink to={activitiesHref([], tenantId)} style={{ color: '#0a1a3f' }}>
          ← Back to activities
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this activity?"
        message={activityDeleteMessage(activityTitle(detail), count, toursChecked)}
        confirmLabel="Delete activity"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
      <DuplicatePopover
        detail={detail}
        anchorEl={duplicateAnchor}
        onClose={() => setDuplicateAnchor(null)}
        unsavedChanges={count}
      />
      <GoogleRefreshPopover
        detail={detail}
        anchorEl={googleAnchor}
        onClose={() => setGoogleAnchor(null)}
        onApplied={onGoogleApplied}
      />
      {/* Mounted while it may be open or closing — not only while narrow — so a close caused
          by widening still runs its exit, and hands the focus to the dock. */}
      <PreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        restoreFocus={!autoClosed}
        {...(autoClosed ? { onExited: focusDock } : {})}
      >
        <RecordPreviewPane />
      </PreviewDrawer>
      {prompt}
    </Box>
  );
}
